<template>
  <div class="bioband-calculator-modal">
    <!-- Modal Header -->
    <div class="modal-header">
      <div class="modal-title">
        <i class="bi bi-rulers"></i>
        <h3>Bio-Banding & Growth</h3>
      </div>
      <button class="close-btn" @click="$emit('close')">&times;</button>
    </div>

    <!-- Tabs -->
    <div class="modal-tabs">
      <button class="tab-btn" :class="{ active: activeTab === 'calculator' }" @click="activeTab = 'calculator'">
        <i class="bi bi-calculator"></i> Bio-Banding Calculator
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'growth' }" @click="activeTab = 'growth'">
        <i class="bi bi-graph-up-arrow"></i> Growth Tracking
      </button>
    </div>

    <!-- Modal Body -->
    <div class="modal-body">
      <!-- Bio-Banding Calculator Tab -->
      <div v-show="activeTab === 'calculator'">
        <!-- Player Info -->
        <div v-if="playerName" class="player-info-bar">
          <i class="bi bi-person-fill"></i>
          <span>Calculating for: <strong>{{ playerName }}</strong></span>
        </div>

        <!-- Measurement Toggle -->
        <div class="measurement-toggle">
          <label class="toggle-option" :class="{ active: !isMetricMode }" @click="switchToImperial">
            <input type="radio" name="measurement-mode" :checked="!isMetricMode" />
            <span>Imperial</span>
          </label>
          <label class="toggle-option" :class="{ active: isMetricMode }" @click="switchToMetric">
            <input type="radio" name="measurement-mode" :checked="isMetricMode" />
            <span>Metric</span>
          </label>
        </div>

        <!-- Imperial Input Form -->
        <div v-show="!isMetricMode" class="bioband-form">
          <div class="form-row">
            <div class="form-group">
              <label>Father's Height</label>
              <div class="input-group">
                <input v-model.number="imperialInputs.fathersHeightFt" type="number" class="form-control" min="1"
                  max="7" placeholder="ft" />
                <input v-model.number="imperialInputs.fathersHeightIn" type="number" step="any" class="form-control"
                  min="0" max="12" placeholder="in" />
              </div>
            </div>
            <div class="form-group">
              <label>Mother's Height</label>
              <div class="input-group">
                <input v-model.number="imperialInputs.mothersHeightFt" type="number" class="form-control" min="1"
                  max="7" placeholder="ft" />
                <input v-model.number="imperialInputs.mothersHeightIn" type="number" step="any" class="form-control"
                  min="0" max="12" placeholder="in" />
              </div>
            </div>
            <div class="form-group">
              <label>Child's DOB</label>
              <input v-model="imperialInputs.childsDob" type="date" class="form-control" :min="minDate"
                :max="maxDate" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Child's Height</label>
              <div class="input-group">
                <input v-model.number="imperialInputs.childsHeightFt" type="number" class="form-control" min="1" max="7"
                  placeholder="ft" />
                <input v-model.number="imperialInputs.childsHeightIn" type="number" step="any" class="form-control"
                  min="0" max="12" placeholder="in" />
              </div>
            </div>
            <div class="form-group">
              <label>Sitting Height <span class="optional-label">(optional)</span></label>
              <div class="input-group">
                <input v-model.number="imperialInputs.sittingHeightFt" type="number" class="form-control" min="1"
                  max="4" placeholder="ft" />
                <input v-model.number="imperialInputs.sittingHeightIn" type="number" step="any" class="form-control"
                  min="0" max="12" placeholder="in" />
              </div>
              <small class="form-hint">For maturity timing (PHV) calculation</small>
            </div>
            <div class="form-group">
              <label>Child's Weight</label>
              <div class="input-group">
                <input v-model.number="imperialInputs.childsWeightSt" type="number" class="form-control" min="1"
                  max="68" placeholder="st" />
                <input v-model.number="imperialInputs.childsWeightLb" type="number" step="any" class="form-control"
                  min="0" max="14" placeholder="lb" />
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Gender</label>
              <div class="gender-toggle">
                <label class="gender-option">
                  <input v-model="imperialInputs.gender" type="radio" value="M" />
                  <span><i class="bi bi-gender-male"></i> Male</span>
                </label>
                <label class="gender-option">
                  <input v-model="imperialInputs.gender" type="radio" value="F" />
                  <span><i class="bi bi-gender-female"></i> Female</span>
                </label>
              </div>
            </div>
            <div class="form-group form-group-btn">
              <button class="btn btn-success calculate-btn" @click="calculateImperial" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm"></span>
                <i v-else class="bi bi-calculator"></i>
                {{ loading ? 'Calculating...' : 'Calculate Bio Age' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Metric Input Form -->
        <div v-show="isMetricMode" class="bioband-form">
          <div class="form-row">
            <div class="form-group">
              <label>Father's Height</label>
              <div class="input-group">
                <input v-model.number="metricInputs.fathersHeight" type="number" step="any" class="form-control"
                  min="30" max="251" placeholder="cm" />
              </div>
            </div>
            <div class="form-group">
              <label>Mother's Height</label>
              <div class="input-group">
                <input v-model.number="metricInputs.mothersHeight" type="number" step="any" class="form-control"
                  min="30" max="251" placeholder="cm" />
              </div>
            </div>
            <div class="form-group">
              <label>Child's DOB</label>
              <input v-model="metricInputs.childsDob" type="date" class="form-control" :min="minDate" :max="maxDate" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Child's Height</label>
              <div class="input-group">
                <input v-model.number="metricInputs.childsHeight" type="number" step="any" class="form-control" min="30"
                  max="251" placeholder="cm" />
              </div>
            </div>
            <div class="form-group">
              <label>Sitting Height <span class="optional-label">(optional)</span></label>
              <div class="input-group">
                <input v-model.number="metricInputs.sittingHeight" type="number" step="any" class="form-control"
                  min="30" max="130" placeholder="cm" />
              </div>
              <small class="form-hint">For maturity timing (PHV) calculation</small>
            </div>
            <div class="form-group">
              <label>Child's Weight</label>
              <div class="input-group">
                <input v-model.number="metricInputs.childsWeight" type="number" step="any" class="form-control" min="1"
                  max="442" placeholder="kg" />
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Gender</label>
              <div class="gender-toggle">
                <label class="gender-option">
                  <input v-model="metricInputs.gender" type="radio" value="M" />
                  <span><i class="bi bi-gender-male"></i> Male</span>
                </label>
                <label class="gender-option">
                  <input v-model="metricInputs.gender" type="radio" value="F" />
                  <span><i class="bi bi-gender-female"></i> Female</span>
                </label>
              </div>
            </div>
            <div class="form-group form-group-btn">
              <button class="btn btn-success calculate-btn" @click="calculateMetric" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm"></span>
                <i v-else class="bi bi-calculator"></i>
                {{ loading ? 'Calculating...' : 'Calculate Bio Age' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="error-pane">
          <div class="alert alert-danger">
            <i class="bi bi-exclamation-circle"></i>
            <span>{{ errorMessage }}</span>
          </div>
        </div>

        <!-- Results Panel -->
        <div v-if="showResults && bioResults" class="bioband-results">
          <h4 class="results-title">
            <i class="bi bi-graph-up"></i>
            Results for {{ playerName }}
          </h4>

          <!-- Height Comparison -->
          <div class="results-section">
            <h5>Estimated Adult Height</h5>
            <div class="heights-display">
              <div class="height-item">
                <div class="height-figure father" :style="{ height: getHeightPercent(bioInputs.fathersHeight) + '%' }">
                  <svg viewBox="250 50 600 1500" preserveAspectRatio="xMidYMax meet">
                    <g transform="matrix(1.3333333,0,0,-1.3333333,0,1600)">
                      <g transform="scale(0.1)">
                        <path
                          d="m 5892.55,6114.67 c 13.77,456.2 3.09,912.57 -27.09,1367.98 -12.68,191.43 -12.21,335.47 -54.9,499.54 -33.52,128.85 -97.3,268.31 -89.09,404 15.19,250.76 -11.93,535.86 -99.01,778.58 -66.27,184.72 -224.21,335.97 -394.53,424.86 -119.24,62.23 -246.92,107.6 -364.23,173.39 -67.6,33.8 -157.04,89.55 -228.36,155.16 -80.83,74.36 -141.23,151.42 -172.13,233.52 -3.16,8.4 -4.82,16.5 -5.32,24.3 0.9,28.1 16.3,52.9 32.4,77.5 20.01,30.5 42.23,65.7 57.29,101 13.48,31.7 21.67,73.3 47.7,99.4 9.21,9.3 18.13,-11.2 41.44,8.9 18.38,15.8 29.04,43.2 34.92,64.9 5.8,21.4 7.1,43.8 12.92,66 9.9,37.9 23.97,76.1 30.87,113 4.53,24.3 8.95,61.2 -11.61,80.8 -9.01,8.5 -37.51,24.5 -43.91,3.3 43.75,145.1 65.75,320.4 48.23,471.8 -6.36,54.9 -22.95,120.3 -57.03,165 -23.25,30.5 -103.76,46.3 -48.53,72.4 -35.37,14.8 -64.47,1.4 -98.34,6.8 -38.75,6.2 -17.57,-3.6 -45.67,35.1 -18.77,25.8 -34.37,26.6 -60.74,39.2 -26.19,12.6 -22.66,11.8 -43.68,33.6 -23.57,-24.6 -27.45,-11.8 -50.23,-9.3 -30.22,3.2 -62.61,-2.3 -92.32,1.9 -33.85,4.9 -60.8,24.7 -96.41,24.9 -35.63,0.2 -61.37,-12 -91.51,-28.8 -44.15,-24.5 -93.22,-21.8 -135.04,-50.6 -46.37,-32 -97.37,-55 -134.6,-100.2 -34.68,-42.1 -70.09,-78.9 -84.14,-132.1 -10.45,-39.5 -20.17,-79.9 -28.25,-120 -25.79,-128.1 -13.33,-284.1 28.78,-407.6 -99.58,17.9 -30.62,-167.7 -24.21,-207.1 10.42,-64 35.97,-133 75.9,-131.9 11.08,0.4 15.68,9.8 20.58,7.8 21.73,-8.9 29.11,-48.4 36.32,-67.7 19.97,-53.4 24.37,-66.4 52.3,-116.1 18.47,-32.8 45.81,-63.5 47.46,-103.2 -4.29,-101.9 -83.76,-179.98 -155.71,-244.46 -76.1,-68.19 -162.01,-117.58 -250.52,-167.22 -117.31,-65.79 -244.99,-111.16 -364.23,-173.39 -170.32,-88.89 -328.26,-240.14 -394.53,-424.86 -87.08,-242.72 -114.2,-527.82 -99.01,-778.58 8.21,-135.69 -55.57,-275.15 -89.09,-404 -42.69,-164.07 -42.22,-308.11 -54.9,-499.54 -30.18,-455.41 -40.86,-911.78 -27.09,-1367.98 -35.37,-150.74 -67.29,-300.88 -63.37,-457.66 4.48,-179.49 91.37,-432.7 245.89,-556.87 37.72,-30.31 95.19,3.75 83.06,50.59 -1.36,5.26 -3.56,10.17 -6.79,14.53 -47.84,64.38 -68.86,110.31 -96.15,185.74 -22.24,61.47 -42.1,145.51 -23.58,210.54 12.73,44.7 56.77,74.59 89.76,32.58 34.21,-43.56 16.11,-168.21 18.61,-222.48 1.03,-22.47 2.76,-46.76 17.39,-63.85 54.47,-63.63 84.03,61.99 89.26,98.1 14.2,98.24 -5.23,203.03 -13.14,301.22 -6.91,85.64 -12.68,272.48 -15.03,276.56 2.95,28.8 6.87,57.89 11.97,87.34 0.01,0 1.87,0 1.87,0 0,0 10.39,63.38 114.89,533.17 65.87,296.15 32.46,290.84 129.46,580.3 68.86,205.44 149.98,410.67 174.81,627.46 10.6,92.54 1.45,182.46 -0.44,274.97 -0.48,23.23 -11.45,163.09 30.96,163.09 20.38,0 33.89,-205.01 42.11,-439.97 8.66,-247.74 67.61,-485.95 66.48,-723.79 -1.12,-237.84 -62.32,-475.31 -93.11,-721.6 -38.64,-309.08 -85,-625.88 -15.45,-1228.58 40.05,-347.12 125.03,-700.39 148.29,-1054.16 24.41,-371.42 -66.8,-738.84 -37.96,-1109.34 25.95,-333.54 100.63,-663.26 221.21,-975.36 60.05,-155.4 110.66,-310.2 122.71,-476.3 3.16,-43.6 7.5,-87.3 12.51,-131 h -0.03 c 0.61,-5.4 1.29,-10.7 1.93,-16.1 1.28,-10.8 2.53,-21.6 3.9,-32.4 2.25,-18 4.58,-35.9 7.04,-53.7 11.54,-140.8 6.21,-287.798 -46.37,-422.001 -55.79,-142.398 -98.01,-238.297 -103.05,-305.297 -8.48,-112.8 78.86,-139.3 242.17,-167.601 226.67,-39.301 257.51,254.5 251.07,417.5 -3.27,82.801 30.44,208.499 26.04,291.399 0.3,0.2 0.5,0.4 0.48,0.8 0,0 -2.76,57.5 -6.92,147.5 h 0.02 c -0.03,0.7 -0.06,1.3 -0.09,2 -0.39,12.4 -0.79,24.8 -1.05,37.1 h -0.67 c -13.43,292.3 -17.64,841.5 -17.64,1003.45 0,283.15 56.68,424.12 19.24,847.82 -17.71,200.47 -91,412.48 -62.42,612.57 15.46,108.18 92.73,370.9 123.63,811.33 9.85,140.29 20.27,536.01 30.93,726.75 10.65,-190.74 21.07,-586.46 30.92,-726.75 30.9,-440.43 108.17,-703.15 123.63,-811.33 28.58,-200.09 -38.04,-412.81 -62.42,-612.57 -46.83,-383.7 19.24,-564.67 19.24,-847.82 0,-161.95 -4.21,-711.15 -17.64,-1003.45 h -0.67 c -0.26,-12.3 -0.66,-24.7 -1.05,-37.1 -0.03,-0.7 -0.06,-1.3 -0.09,-2 h 0.9 c -4.16,-90 -6.92,-147.5 -6.92,-147.5 -0.02,-0.4 0.18,-0.6 0.48,-0.8 -4.41,-82.9 29.31,-208.598 26.04,-291.399 -6.44,-163 24.4,-456.801 251.07,-417.5 163.31,28.301 250.65,54.801 242.17,167.601 -5.05,67 -47.26,162.899 -103.05,305.297 -52.58,134.203 -57.92,281.201 -46.37,422.001 4.67,33.9 8.96,68 12.87,102.2 h -0.91 c 5.01,43.7 9.35,87.4 12.51,131 12.05,166.1 62.66,320.9 122.71,476.3 120.58,312.1 195.26,641.82 221.21,975.36 28.84,370.5 -62.37,737.92 -37.95,1109.34 23.25,353.77 108.23,707.04 148.28,1054.16 69.55,602.7 23.19,919.5 -15.45,1228.58 -30.79,246.29 -91.99,483.76 -93.12,721.6 -1.12,237.84 57.83,476.05 66.49,723.79 8.22,234.96 21.73,439.97 42.11,439.97 42.41,0 31.44,-139.86 30.96,-163.09 -1.89,-92.51 -11.04,-182.43 -0.44,-274.97 24.83,-216.79 105.95,-422.02 174.81,-627.46 97.01,-289.46 63.59,-284.15 129.47,-580.3 104.49,-469.79 114.87,-533.17 114.87,-533.17 0,0 1.86,0 1.88,0 5.1,-29.45 9.02,-58.54 11.97,-87.34 -2.35,-4.08 -8.12,-190.92 -15.03,-276.56 -7.91,-98.19 -27.34,-202.98 -13.14,-301.22 5.23,-36.11 34.79,-161.73 89.26,-98.1 14.63,17.09 16.36,41.38 17.39,63.85 2.5,54.27 -15.6,178.92 18.61,222.48 32.99,42.01 77.03,12.12 89.76,-32.58 18.52,-65.03 -1.34,-149.07 -23.58,-210.54 -27.29,-75.43 -48.31,-121.36 -96.15,-185.74 -3.23,-4.36 -5.43,-9.27 -6.79,-14.53 -12.13,-46.84 45.34,-80.9 83.06,-50.59 154.52,124.17 241.4,377.38 245.89,556.87 3.92,156.78 -28,306.92 -63.37,457.66"
                          style="fill:#fd85a0;fill-opacity:1;fill-rule:nonzero;stroke:none;" />
                      </g>
                    </g>
                  </svg>
                </div>
                <span class="height-label">Father</span>
                <span class="height-value">{{ Math.round(bioInputs.fathersHeight) }} cm</span>
              </div>
              <div class="height-item">
                <div class="height-figure mother" :style="{ height: getHeightPercent(bioInputs.mothersHeight) + '%' }">
                  <svg class="mother-height-human" id="mother-height-human" height="144.67" viewBox="800 125 600 1420"
                    data-v-59c7acfa="">
                    <g id="motg158" transform="matrix(1.3333333,0,0,-1.3333333,0,1600)" data-v-59c7acfa="">
                      <g id="motg160" transform="scale(0.1)" data-v-59c7acfa="">
                        <path
                          d="m 9688.49,6279.45 c -13.98,118.85 0,573.25 -34.96,762.01 -34.95,188.75 -69.91,286.62 -97.87,363.52 -27.96,76.9 -27.96,293.61 -48.93,475.38 -34.44,298.47 -9.3,633.87 -102.65,924.69 -112.88,351.63 -379.24,303.09 -602.97,394.17 -103.24,42.03 -196.68,97.51 -259.66,192.19 -6.71,10.1 -21.47,47.71 -37.05,93.15 h 33.6 c -1.91,2.17 -3.82,4.36 -5.67,6.6 2.73,0.47 5.45,1.02 8.17,1.5 1.96,0.3 3.91,0.66 5.82,1.07 8.55,1.55 17.09,3.05 25.63,4.71 h -11.17 c 7.53,3.51 14.04,8.63 18.61,16.18 -31.05,4.05 -59.73,24.92 -74.43,54.18 79.32,-29.77 174,-6.68 233.04,56.84 2.51,2.7 5.16,6.37 3.88,9.92 -0.78,2.17 -2.81,3.49 -4.72,4.65 -8.6,5.23 -103.87,75.93 -46.93,71.3 34.31,-2.8 112.8,-3.57 134.61,26.27 -17.46,16.47 -39.29,28.55 -54.26,48.21 -1.86,2.44 -25.82,33.14 -11.42,22.97 22.97,-16.22 69.15,-27.66 94.98,-22.28 -29.08,24.19 -55.83,48 -68.15,87.26 -7.64,24.36 49.83,73.09 65.16,198.96 15.33,125.9 -15.33,280.2 -65.15,430.4 -49.83,150.2 -95.82,320.8 -275.96,418.2 -180.13,97.5 -287.45,28.4 -287.45,28.4 0,0 -107.31,69.1 -287.45,-28.4 -180.13,-97.4 -226.12,-268 -275.95,-418.2 -49.82,-150.2 -80.48,-304.5 -65.15,-430.4 15.33,-125.87 72.8,-174.6 65.16,-198.96 -12.32,-39.26 -39.07,-63.07 -68.14,-87.26 25.82,-5.38 71.99,6.06 94.97,22.28 14.4,10.17 -9.56,-20.53 -11.42,-22.97 -14.97,-19.66 -36.8,-31.74 -54.26,-48.21 21.81,-29.84 100.3,-29.07 134.61,-26.27 56.94,4.63 -38.33,-66.07 -46.93,-71.3 -1.9,-1.16 -3.94,-2.48 -4.72,-4.65 -1.28,-3.55 1.37,-7.22 3.88,-9.92 59.04,-63.52 153.72,-86.61 233.04,-56.84 -14.7,-29.26 -43.38,-50.13 -74.43,-54.18 10.81,-17.85 32.41,-22.25 53.05,-23.38 v -6.68 h 21.94 c -15.58,-45.44 -30.34,-83.05 -37.05,-93.15 -62.98,-94.68 -156.42,-150.16 -259.66,-192.19 -223.74,-91.08 -490.1,-42.54 -602.97,-394.17 -93.35,-290.82 -68.21,-626.22 -102.65,-924.69 -20.97,-181.77 -20.97,-398.48 -48.93,-475.38 -27.96,-76.9 -62.92,-174.77 -97.87,-363.52 -34.96,-188.76 -20.98,-643.16 -34.96,-762.01 -13.98,-118.84 -13.98,-286.61 0,-510.32 6.46,-103.33 12.28,-211.18 28.3,-315.25 13.71,-89.06 44.79,-176.28 87.05,-255.71 17.69,-33.25 39.18,-65.08 61.43,-95.41 22.11,-30.15 71.65,-112.23 117.66,-100.33 11.66,3.02 20.18,13.81 23.17,25.48 8.9,34.71 -29.84,79.21 -44.3,108.9 -24.43,50.17 -38.96,102.54 -50.35,156.91 -6.89,32.9 -7.86,59.67 2.71,71.7 34.38,39.17 72.98,-193.35 120.01,-186.44 31.48,4.62 22.64,81.53 13.58,178.27 -9.14,97.64 -9.2,212.34 -50.02,303.34 -38.47,85.77 -95.36,170.99 -85.53,269.32 13.98,139.82 181.76,713.07 202.74,901.82 20.97,188.75 65.92,629.18 100.88,741.03 34.8,111.37 82.34,402.95 82.83,405.45 -2.63,-13.35 112.1,-151.76 123.82,-176.97 50.42,-108.43 65.33,-246.17 75.55,-363.98 36.86,-425.05 -133.11,-793.43 -243.27,-1192.76 -55.93,-202.74 -111.85,-713.07 -83.89,-908.81 27.96,-195.74 90.88,-608.2 160.79,-796.96 69.91,-188.75 153.8,-517.32 160.79,-629.17 4.74,-75.79 4.39,-280.99 -1.58,-469.97 h -0.04 c -2.84,-89.93 -6.95,-176.22 -12.4,-243.1 -12.7,-155.8 -13.99,-433.43 69.91,-824.94 87.49,-408.25 239.24,-1014.75 239.03,-1357.85 h 0.04 c 0.02,-0.3 -0.01,-0.6 0,-0.9 5.72,-94.198 0.45,-190.3 -34.38,-279.499 -40.2,-103 -60.62,-172.301 -64.25,-220.801 -6.12,-81.602 45.91,-106.898 164.5,-121.199 207.36,-25 195.56,184 190.92,301.898 -2.36,59.903 11.93,150.801 8.76,210.703 0.22,0.2 0.36,0.399 0.35,0.598 0,0 -1.99,41.602 -4.99,106.7 h 0.01 c -0.02,0.5 -0.04,1 -0.06,1.4 -0.01,0.4 -0.02,0.7 -0.03,1.1 h 0.27 c -0.17,2.3 -0.3,4.7 -0.47,7 -0.04,1.7 -0.09,3.3 -0.14,5 h -0.2 c -41.22,572.6 56.34,1151.46 53.73,1724.7 -1.37,300.3 -26.04,468.31 -33.82,673.92 -0.19,5.06 -0.29,10.16 -0.41,15.27 h 0.05 c -2.31,106.63 12.01,224.25 4.87,375.97 -10.41,220.95 -17.37,351.41 -4.59,652.17 6.97,164.16 60.97,419.12 57.63,578.73 -1.88,89.99 14.54,208.46 29.23,294.81 14.7,-86.35 31.12,-204.82 29.24,-294.81 -3.34,-159.61 48.56,-414.67 57.63,-578.73 13.67,-247.34 5.82,-431.22 -4.59,-652.17 -7.14,-151.72 7.18,-269.34 4.87,-375.97 h -0.21 c -0.11,-5.11 -0.22,-10.21 -0.41,-15.27 -7.77,-205.61 -32.44,-373.62 -33.81,-673.92 -2.62,-573.24 94.95,-1152.1 53.72,-1724.7 h -0.2 c -0.04,-1.7 -0.09,-3.3 -0.14,-5 -0.17,-2.3 -0.29,-4.7 -0.47,-7 h 0.28 c -0.01,-0.4 -0.02,-0.7 -0.03,-1.1 -0.02,-0.4 -0.05,-0.9 -0.07,-1.4 h 0.02 c -3,-65.098 -4.99,-106.7 -4.99,-106.7 -0.01,-0.199 0.13,-0.398 0.34,-0.598 -3.17,-59.902 11.13,-150.8 8.77,-210.703 -4.64,-117.898 -16.45,-326.898 190.92,-301.898 118.58,14.301 170.62,39.597 164.5,121.199 -3.63,48.5 -24.05,117.801 -64.26,220.801 -34.82,89.199 -40.09,185.301 -34.38,279.499 0.02,0.3 -0.01,0.6 0,0.9 h 0.05 c -0.21,343.1 151.54,949.6 239.03,1357.85 83.89,391.51 82.61,669.14 69.91,824.94 -5.45,66.88 -9.56,153.17 -12.4,243.1 h 0.21 c -5.97,188.98 -6.32,394.18 -1.58,469.97 6.99,111.85 90.88,440.42 160.79,629.17 69.91,188.76 132.83,601.22 160.79,796.96 27.96,195.74 -27.96,706.07 -83.89,908.81 -110.16,399.33 -280.13,767.71 -243.27,1192.76 10.22,117.81 25.13,255.55 75.55,363.98 11.72,25.21 126.45,163.62 123.82,176.97 0.49,-2.5 58.03,-294.08 92.83,-405.45 34.96,-111.85 69.91,-552.28 90.89,-741.03 20.97,-188.75 188.75,-762 202.73,-901.82 9.83,-98.33 -47.07,-183.55 -85.53,-269.32 -40.82,-91 -40.88,-205.7 -50.02,-303.34 -9.06,-96.74 -17.9,-173.65 13.58,-178.27 47.03,-6.91 85.63,225.61 120.01,186.44 10.57,-12.03 9.6,-38.8 2.71,-71.7 -11.39,-54.37 -25.92,-106.74 -50.35,-156.91 -14.46,-29.69 -53.2,-74.19 -44.3,-108.9 2.99,-11.67 11.51,-22.46 23.17,-25.48 46.01,-11.9 95.55,70.18 117.66,100.33 22.25,30.33 43.74,62.16 61.43,95.41 42.26,79.43 73.34,166.65 87.05,255.71 16.02,104.07 21.84,211.92 28.3,315.25 13.98,223.71 13.98,391.48 0,510.32"
                          style="fill:#b39fdb;fill-opacity:1;fill-rule:nonzero;stroke:none;" id="motpath166"
                          data-v-59c7acfa=""></path>
                      </g>
                    </g>
                  </svg>
                </div>
                <span class="height-label">Mother</span>
                <span class="height-value">{{ Math.round(bioInputs.mothersHeight) }} cm</span>
              </div>
              <div class="height-item">
                <div class="height-figure child" :class="bioInputs.gender === 'M' ? 'male' : 'female'"
                  :style="{ height: getHeightPercent(bioInputs.childsHeight) + '%' }">
                  <svg data-v-59c7acfa="" class="child-height-human" id="child-height-male-human" height="149.005"
                    viewBox="250 50 600 1500" style="display:flex">
                    <g data-v-59c7acfa="" id="chig158" transform="matrix(1.3333333,0,0,-1.3333333,0,1600)">
                      <g data-v-59c7acfa="" id="chig160" transform="scale(0.1)">
                        <path data-v-59c7acfa=""
                          d="m 5892.55,6114.67 c 13.77,456.2 3.09,912.57 -27.09,1367.98 -12.68,191.43 -12.21,335.47 -54.9,499.54 -33.52,128.85 -97.3,268.31 -89.09,404 15.19,250.76 -11.93,535.86 -99.01,778.58 -66.27,184.72 -224.21,335.97 -394.53,424.86 -119.24,62.23 -246.92,107.6 -364.23,173.39 -67.6,33.8 -157.04,89.55 -228.36,155.16 -80.83,74.36 -141.23,151.42 -172.13,233.52 -3.16,8.4 -4.82,16.5 -5.32,24.3 0.9,28.1 16.3,52.9 32.4,77.5 20.01,30.5 42.23,65.7 57.29,101 13.48,31.7 21.67,73.3 47.7,99.4 9.21,9.3 18.13,-11.2 41.44,8.9 18.38,15.8 29.04,43.2 34.92,64.9 5.8,21.4 7.1,43.8 12.92,66 9.9,37.9 23.97,76.1 30.87,113 4.53,24.3 8.95,61.2 -11.61,80.8 -9.01,8.5 -37.51,24.5 -43.91,3.3 43.75,145.1 65.75,320.4 48.23,471.8 -6.36,54.9 -22.95,120.3 -57.03,165 -23.25,30.5 -103.76,46.3 -48.53,72.4 -35.37,14.8 -64.47,1.4 -98.34,6.8 -38.75,6.2 -17.57,-3.6 -45.67,35.1 -18.77,25.8 -34.37,26.6 -60.74,39.2 -26.19,12.6 -22.66,11.8 -43.68,33.6 -23.57,-24.6 -27.45,-11.8 -50.23,-9.3 -30.22,3.2 -62.61,-2.3 -92.32,1.9 -33.85,4.9 -60.8,24.7 -96.41,24.9 -35.63,0.2 -61.37,-12 -91.51,-28.8 -44.15,-24.5 -93.22,-21.8 -135.04,-50.6 -46.37,-32 -97.37,-55 -134.6,-100.2 -34.68,-42.1 -70.09,-78.9 -84.14,-132.1 -10.45,-39.5 -20.17,-79.9 -28.25,-120 -25.79,-128.1 -13.33,-284.1 28.78,-407.6 -99.58,17.9 -30.62,-167.7 -24.21,-207.1 10.42,-64 35.97,-133 75.9,-131.9 11.08,0.4 15.68,9.8 20.58,7.8 21.73,-8.9 29.11,-48.4 36.32,-67.7 19.97,-53.4 24.37,-66.4 52.3,-116.1 18.47,-32.8 45.81,-63.5 47.46,-103.2 -4.29,-101.9 -83.76,-179.98 -155.71,-244.46 -76.1,-68.19 -162.01,-117.58 -250.52,-167.22 -117.31,-65.79 -244.99,-111.16 -364.23,-173.39 -170.32,-88.89 -328.26,-240.14 -394.53,-424.86 -87.08,-242.72 -114.2,-527.82 -99.01,-778.58 8.21,-135.69 -55.57,-275.15 -89.09,-404 -42.69,-164.07 -42.22,-308.11 -54.9,-499.54 -30.18,-455.41 -40.86,-911.78 -27.09,-1367.98 -35.37,-150.74 -67.29,-300.88 -63.37,-457.66 4.48,-179.49 91.37,-432.7 245.89,-556.87 37.72,-30.31 95.19,3.75 83.06,50.59 -1.36,5.26 -3.56,10.17 -6.79,14.53 -47.84,64.38 -68.86,110.31 -96.15,185.74 -22.24,61.47 -42.1,145.51 -23.58,210.54 12.73,44.7 56.77,74.59 89.76,32.58 34.21,-43.56 16.11,-168.21 18.61,-222.48 1.03,-22.47 2.76,-46.76 17.39,-63.85 54.47,-63.63 84.03,61.99 89.26,98.1 14.2,98.24 -5.23,203.03 -13.14,301.22 -6.91,85.64 -12.68,272.48 -15.03,276.56 2.95,28.8 6.87,57.89 11.97,87.34 0.01,0 1.87,0 1.87,0 0,0 10.39,63.38 114.89,533.17 65.87,296.15 32.46,290.84 129.46,580.3 68.86,205.44 149.98,410.67 174.81,627.46 10.6,92.54 1.45,182.46 -0.44,274.97 -0.48,23.23 -11.45,163.09 30.96,163.09 20.38,0 33.89,-205.01 42.11,-439.97 8.66,-247.74 67.61,-485.95 66.48,-723.79 -1.12,-237.84 -62.32,-475.31 -93.11,-721.6 -38.64,-309.08 -85,-625.88 -15.45,-1228.58 40.05,-347.12 125.03,-700.39 148.29,-1054.16 24.41,-371.42 -66.8,-738.84 -37.96,-1109.34 25.95,-333.54 100.63,-663.26 221.21,-975.36 60.05,-155.4 110.66,-310.2 122.71,-476.3 3.16,-43.6 7.5,-87.3 12.51,-131 h -0.03 c 0.61,-5.4 1.29,-10.7 1.93,-16.1 1.28,-10.8 2.53,-21.6 3.9,-32.4 2.25,-18 4.58,-35.9 7.04,-53.7 11.54,-140.8 6.21,-287.798 -46.37,-422.001 -55.79,-142.398 -98.01,-238.297 -103.05,-305.297 -8.48,-112.8 78.86,-139.3 242.17,-167.601 226.67,-39.301 257.51,254.5 251.07,417.5 -3.27,82.801 30.44,208.499 26.04,291.399 0.3,0.2 0.5,0.4 0.48,0.8 0,0 -2.76,57.5 -6.92,147.5 h 0.02 c -0.03,0.7 -0.06,1.3 -0.09,2 -0.39,12.4 -0.79,24.8 -1.05,37.1 h -0.67 c -13.43,292.3 -17.64,841.5 -17.64,1003.45 0,283.15 56.68,424.12 19.24,847.82 -17.71,200.47 -91,412.48 -62.42,612.57 15.46,108.18 92.73,370.9 123.63,811.33 9.85,140.29 20.27,536.01 30.93,726.75 10.65,-190.74 21.07,-586.46 30.92,-726.75 30.9,-440.43 108.17,-703.15 123.63,-811.33 28.58,-200.09 -38.04,-412.81 -62.42,-612.57 -46.83,-383.7 19.24,-564.67 19.24,-847.82 0,-161.95 -4.21,-711.15 -17.64,-1003.45 h -0.67 c -0.26,-12.3 -0.66,-24.7 -1.05,-37.1 -0.03,-0.7 -0.06,-1.3 -0.09,-2 h 0.9 c -4.16,-90 -6.92,-147.5 -6.92,-147.5 -0.02,-0.4 0.18,-0.6 0.48,-0.8 -4.41,-82.9 29.31,-208.598 26.04,-291.399 -6.44,-163 24.4,-456.801 251.07,-417.5 163.31,28.301 250.65,54.801 242.17,167.601 -5.05,67 -47.26,162.899 -103.05,305.297 -52.58,134.203 -57.92,281.201 -46.37,422.001 4.67,33.9 8.96,68 12.87,102.2 h -0.91 c 5.01,43.7 9.35,87.4 12.51,131 12.05,166.1 62.66,320.9 122.71,476.3 120.58,312.1 195.26,641.82 221.21,975.36 28.84,370.5 -62.37,737.92 -37.95,1109.34 23.25,353.77 108.23,707.04 148.28,1054.16 69.55,602.7 23.19,919.5 -15.45,1228.58 -30.79,246.29 -91.99,483.76 -93.12,721.6 -1.12,237.84 57.83,476.05 66.49,723.79 8.22,234.96 21.73,439.97 42.11,439.97 42.41,0 31.44,-139.86 30.96,-163.09 -1.89,-92.51 -11.04,-182.43 -0.44,-274.97 24.83,-216.79 105.95,-422.02 174.81,-627.46 97.01,-289.46 63.59,-284.15 129.47,-580.3 104.49,-469.79 114.87,-533.17 114.87,-533.17 0,0 1.86,0 1.88,0 5.1,-29.45 9.02,-58.54 11.97,-87.34 -2.35,-4.08 -8.12,-190.92 -15.03,-276.56 -7.91,-98.19 -27.34,-202.98 -13.14,-301.22 5.23,-36.11 34.79,-161.73 89.26,-98.1 14.63,17.09 16.36,41.38 17.39,63.85 2.5,54.27 -15.6,178.92 18.61,222.48 32.99,42.01 77.03,12.12 89.76,-32.58 18.52,-65.03 -1.34,-149.07 -23.58,-210.54 -27.29,-75.43 -48.31,-121.36 -96.15,-185.74 -3.23,-4.36 -5.43,-9.27 -6.79,-14.53 -12.13,-46.84 45.34,-80.9 83.06,-50.59 154.52,124.17 241.4,377.38 245.89,556.87 3.92,156.78 -28,306.92 -63.37,457.66"
                          id="chipath164"
                          style="fill: rgb(255, 235, 59); fill-opacity: 1; fill-rule: nonzero; stroke: none;"></path>
                      </g>
                    </g>
                  </svg>
                </div>
                <span class="height-label">Child</span>
                <span class="height-value">{{ Math.round(bioInputs.childsHeight) }} cm</span>
              </div>
              <div class="height-item highlight">
                <div class="height-figure estimated" :class="bioInputs.gender === 'M' ? 'male' : 'female'"
                  :style="{ height: getHeightPercent(bioResults.childEstimatedHeight) + '%' }">
                  <svg data-v-59c7acfa="" class="estimated-height-human" id="estimated-height-male-human"
                    height="156.74" viewBox="250 50 600 1500" style="display:flex">
                    <g data-v-59c7acfa="" id="estg158" transform="matrix(1.3333333,0,0,-1.3333333,0,1600)">
                      <g data-v-59c7acfa="" id="estg160" transform="scale(0.1)">
                        <path data-v-59c7acfa=""
                          d="m 5892.55,6114.67 c 13.77,456.2 3.09,912.57 -27.09,1367.98 -12.68,191.43 -12.21,335.47 -54.9,499.54 -33.52,128.85 -97.3,268.31 -89.09,404 15.19,250.76 -11.93,535.86 -99.01,778.58 -66.27,184.72 -224.21,335.97 -394.53,424.86 -119.24,62.23 -246.92,107.6 -364.23,173.39 -67.6,33.8 -157.04,89.55 -228.36,155.16 -80.83,74.36 -141.23,151.42 -172.13,233.52 -3.16,8.4 -4.82,16.5 -5.32,24.3 0.9,28.1 16.3,52.9 32.4,77.5 20.01,30.5 42.23,65.7 57.29,101 13.48,31.7 21.67,73.3 47.7,99.4 9.21,9.3 18.13,-11.2 41.44,8.9 18.38,15.8 29.04,43.2 34.92,64.9 5.8,21.4 7.1,43.8 12.92,66 9.9,37.9 23.97,76.1 30.87,113 4.53,24.3 8.95,61.2 -11.61,80.8 -9.01,8.5 -37.51,24.5 -43.91,3.3 43.75,145.1 65.75,320.4 48.23,471.8 -6.36,54.9 -22.95,120.3 -57.03,165 -23.25,30.5 -103.76,46.3 -48.53,72.4 -35.37,14.8 -64.47,1.4 -98.34,6.8 -38.75,6.2 -17.57,-3.6 -45.67,35.1 -18.77,25.8 -34.37,26.6 -60.74,39.2 -26.19,12.6 -22.66,11.8 -43.68,33.6 -23.57,-24.6 -27.45,-11.8 -50.23,-9.3 -30.22,3.2 -62.61,-2.3 -92.32,1.9 -33.85,4.9 -60.8,24.7 -96.41,24.9 -35.63,0.2 -61.37,-12 -91.51,-28.8 -44.15,-24.5 -93.22,-21.8 -135.04,-50.6 -46.37,-32 -97.37,-55 -134.6,-100.2 -34.68,-42.1 -70.09,-78.9 -84.14,-132.1 -10.45,-39.5 -20.17,-79.9 -28.25,-120 -25.79,-128.1 -13.33,-284.1 28.78,-407.6 -99.58,17.9 -30.62,-167.7 -24.21,-207.1 10.42,-64 35.97,-133 75.9,-131.9 11.08,0.4 15.68,9.8 20.58,7.8 21.73,-8.9 29.11,-48.4 36.32,-67.7 19.97,-53.4 24.37,-66.4 52.3,-116.1 18.47,-32.8 45.81,-63.5 47.46,-103.2 -4.29,-101.9 -83.76,-179.98 -155.71,-244.46 -76.1,-68.19 -162.01,-117.58 -250.52,-167.22 -117.31,-65.79 -244.99,-111.16 -364.23,-173.39 -170.32,-88.89 -328.26,-240.14 -394.53,-424.86 -87.08,-242.72 -114.2,-527.82 -99.01,-778.58 8.21,-135.69 -55.57,-275.15 -89.09,-404 -42.69,-164.07 -42.22,-308.11 -54.9,-499.54 -30.18,-455.41 -40.86,-911.78 -27.09,-1367.98 -35.37,-150.74 -67.29,-300.88 -63.37,-457.66 4.48,-179.49 91.37,-432.7 245.89,-556.87 37.72,-30.31 95.19,3.75 83.06,50.59 -1.36,5.26 -3.56,10.17 -6.79,14.53 -47.84,64.38 -68.86,110.31 -96.15,185.74 -22.24,61.47 -42.1,145.51 -23.58,210.54 12.73,44.7 56.77,74.59 89.76,32.58 34.21,-43.56 16.11,-168.21 18.61,-222.48 1.03,-22.47 2.76,-46.76 17.39,-63.85 54.47,-63.63 84.03,61.99 89.26,98.1 14.2,98.24 -5.23,203.03 -13.14,301.22 -6.91,85.64 -12.68,272.48 -15.03,276.56 2.95,28.8 6.87,57.89 11.97,87.34 0.01,0 1.87,0 1.87,0 0,0 10.39,63.38 114.89,533.17 65.87,296.15 32.46,290.84 129.46,580.3 68.86,205.44 149.98,410.67 174.81,627.46 10.6,92.54 1.45,182.46 -0.44,274.97 -0.48,23.23 -11.45,163.09 30.96,163.09 20.38,0 33.89,-205.01 42.11,-439.97 8.66,-247.74 67.61,-485.95 66.48,-723.79 -1.12,-237.84 -62.32,-475.31 -93.11,-721.6 -38.64,-309.08 -85,-625.88 -15.45,-1228.58 40.05,-347.12 125.03,-700.39 148.29,-1054.16 24.41,-371.42 -66.8,-738.84 -37.96,-1109.34 25.95,-333.54 100.63,-663.26 221.21,-975.36 60.05,-155.4 110.66,-310.2 122.71,-476.3 3.16,-43.6 7.5,-87.3 12.51,-131 h -0.03 c 0.61,-5.4 1.29,-10.7 1.93,-16.1 1.28,-10.8 2.53,-21.6 3.9,-32.4 2.25,-18 4.58,-35.9 7.04,-53.7 11.54,-140.8 6.21,-287.798 -46.37,-422.001 -55.79,-142.398 -98.01,-238.297 -103.05,-305.297 -8.48,-112.8 78.86,-139.3 242.17,-167.601 226.67,-39.301 257.51,254.5 251.07,417.5 -3.27,82.801 30.44,208.499 26.04,291.399 0.3,0.2 0.5,0.4 0.48,0.8 0,0 -2.76,57.5 -6.92,147.5 h 0.02 c -0.03,0.7 -0.06,1.3 -0.09,2 -0.39,12.4 -0.79,24.8 -1.05,37.1 h -0.67 c -13.43,292.3 -17.64,841.5 -17.64,1003.45 0,283.15 56.68,424.12 19.24,847.82 -17.71,200.47 -91,412.48 -62.42,612.57 15.46,108.18 92.73,370.9 123.63,811.33 9.85,140.29 20.27,536.01 30.93,726.75 10.65,-190.74 21.07,-586.46 30.92,-726.75 30.9,-440.43 108.17,-703.15 123.63,-811.33 28.58,-200.09 -38.04,-412.81 -62.42,-612.57 -46.83,-383.7 19.24,-564.67 19.24,-847.82 0,-161.95 -4.21,-711.15 -17.64,-1003.45 h -0.67 c -0.26,-12.3 -0.66,-24.7 -1.05,-37.1 -0.03,-0.7 -0.06,-1.3 -0.09,-2 h 0.9 c -4.16,-90 -6.92,-147.5 -6.92,-147.5 -0.02,-0.4 0.18,-0.6 0.48,-0.8 -4.41,-82.9 29.31,-208.598 26.04,-291.399 -6.44,-163 24.4,-456.801 251.07,-417.5 163.31,28.301 250.65,54.801 242.17,167.601 -5.05,67 -47.26,162.899 -103.05,305.297 -52.58,134.203 -57.92,281.201 -46.37,422.001 4.67,33.9 8.96,68 12.87,102.2 h -0.91 c 5.01,43.7 9.35,87.4 12.51,131 12.05,166.1 62.66,320.9 122.71,476.3 120.58,312.1 195.26,641.82 221.21,975.36 28.84,370.5 -62.37,737.92 -37.95,1109.34 23.25,353.77 108.23,707.04 148.28,1054.16 69.55,602.7 23.19,919.5 -15.45,1228.58 -30.79,246.29 -91.99,483.76 -93.12,721.6 -1.12,237.84 57.83,476.05 66.49,723.79 8.22,234.96 21.73,439.97 42.11,439.97 42.41,0 31.44,-139.86 30.96,-163.09 -1.89,-92.51 -11.04,-182.43 -0.44,-274.97 24.83,-216.79 105.95,-422.02 174.81,-627.46 97.01,-289.46 63.59,-284.15 129.47,-580.3 104.49,-469.79 114.87,-533.17 114.87,-533.17 0,0 1.86,0 1.88,0 5.1,-29.45 9.02,-58.54 11.97,-87.34 -2.35,-4.08 -8.12,-190.92 -15.03,-276.56 -7.91,-98.19 -27.34,-202.98 -13.14,-301.22 5.23,-36.11 34.79,-161.73 89.26,-98.1 14.63,17.09 16.36,41.38 17.39,63.85 2.5,54.27 -15.6,178.92 18.61,222.48 32.99,42.01 77.03,12.12 89.76,-32.58 18.52,-65.03 -1.34,-149.07 -23.58,-210.54 -27.29,-75.43 -48.31,-121.36 -96.15,-185.74 -3.23,-4.36 -5.43,-9.27 -6.79,-14.53 -12.13,-46.84 45.34,-80.9 83.06,-50.59 154.52,124.17 241.4,377.38 245.89,556.87 3.92,156.78 -28,306.92 -63.37,457.66"
                          id="estpath164"
                          style="fill: rgb(255, 199, 16); fill-opacity: 1; fill-rule: nonzero; stroke: none;"></path>
                      </g>
                    </g>
                  </svg>
                </div>
                <span class="height-label">Estimated</span>
                <span class="height-value">{{ bioResults.childEstimatedHeight }} cm</span>
              </div>
            </div>

            <!-- Height Progress Bar -->
            <div class="progress-section">
              <label>% of Estimated Adult Height Achieved</label>
              <div class="progress">
                <div class="progress-bar" :style="{ width: bioResults.childEstimatedHeightAchieved + '%' }">
                  {{ bioResults.childEstimatedHeightAchieved }}%
                </div>
              </div>
            </div>
          </div>

          <!-- Bio Age Section -->
          <div class="results-section">
            <h5>Biological Age Comparison</h5>
            <div class="age-comparison">
              <div class="age-item">
                <svg data-v-59c7acfa="" class="decimal-age-human" id="decimal-age-male-human" height="167.2"
                  viewBox="250 50 600 1500" style="display:flex">
                  <g data-v-59c7acfa="" id="g159" transform="matrix(1.3333333,0,0,-1.3333333,0,1600)">
                    <g data-v-59c7acfa="" id="g161" transform="scale(0.1)">
                      <path data-v-59c7acfa=""
                        d="m 5892.55,6114.67 c 13.77,456.2 3.09,912.57 -27.09,1367.98 -12.68,191.43 -12.21,335.47 -54.9,499.54 -33.52,128.85 -97.3,268.31 -89.09,404 15.19,250.76 -11.93,535.86 -99.01,778.58 -66.27,184.72 -224.21,335.97 -394.53,424.86 -119.24,62.23 -246.92,107.6 -364.23,173.39 -67.6,33.8 -157.04,89.55 -228.36,155.16 -80.83,74.36 -141.23,151.42 -172.13,233.52 -3.16,8.4 -4.82,16.5 -5.32,24.3 0.9,28.1 16.3,52.9 32.4,77.5 20.01,30.5 42.23,65.7 57.29,101 13.48,31.7 21.67,73.3 47.7,99.4 9.21,9.3 18.13,-11.2 41.44,8.9 18.38,15.8 29.04,43.2 34.92,64.9 5.8,21.4 7.1,43.8 12.92,66 9.9,37.9 23.97,76.1 30.87,113 4.53,24.3 8.95,61.2 -11.61,80.8 -9.01,8.5 -37.51,24.5 -43.91,3.3 43.75,145.1 65.75,320.4 48.23,471.8 -6.36,54.9 -22.95,120.3 -57.03,165 -23.25,30.5 -103.76,46.3 -48.53,72.4 -35.37,14.8 -64.47,1.4 -98.34,6.8 -38.75,6.2 -17.57,-3.6 -45.67,35.1 -18.77,25.8 -34.37,26.6 -60.74,39.2 -26.19,12.6 -22.66,11.8 -43.68,33.6 -23.57,-24.6 -27.45,-11.8 -50.23,-9.3 -30.22,3.2 -62.61,-2.3 -92.32,1.9 -33.85,4.9 -60.8,24.7 -96.41,24.9 -35.63,0.2 -61.37,-12 -91.51,-28.8 -44.15,-24.5 -93.22,-21.8 -135.04,-50.6 -46.37,-32 -97.37,-55 -134.6,-100.2 -34.68,-42.1 -70.09,-78.9 -84.14,-132.1 -10.45,-39.5 -20.17,-79.9 -28.25,-120 -25.79,-128.1 -13.33,-284.1 28.78,-407.6 -99.58,17.9 -30.62,-167.7 -24.21,-207.1 10.42,-64 35.97,-133 75.9,-131.9 11.08,0.4 15.68,9.8 20.58,7.8 21.73,-8.9 29.11,-48.4 36.32,-67.7 19.97,-53.4 24.37,-66.4 52.3,-116.1 18.47,-32.8 45.81,-63.5 47.46,-103.2 -4.29,-101.9 -83.76,-179.98 -155.71,-244.46 -76.1,-68.19 -162.01,-117.58 -250.52,-167.22 -117.31,-65.79 -244.99,-111.16 -364.23,-173.39 -170.32,-88.89 -328.26,-240.14 -394.53,-424.86 -87.08,-242.72 -114.2,-527.82 -99.01,-778.58 8.21,-135.69 -55.57,-275.15 -89.09,-404 -42.69,-164.07 -42.22,-308.11 -54.9,-499.54 -30.18,-455.41 -40.86,-911.78 -27.09,-1367.98 -35.37,-150.74 -67.29,-300.88 -63.37,-457.66 4.48,-179.49 91.37,-432.7 245.89,-556.87 37.72,-30.31 95.19,3.75 83.06,50.59 -1.36,5.26 -3.56,10.17 -6.79,14.53 -47.84,64.38 -68.86,110.31 -96.15,185.74 -22.24,61.47 -42.1,145.51 -23.58,210.54 12.73,44.7 56.77,74.59 89.76,32.58 34.21,-43.56 16.11,-168.21 18.61,-222.48 1.03,-22.47 2.76,-46.76 17.39,-63.85 54.47,-63.63 84.03,61.99 89.26,98.1 14.2,98.24 -5.23,203.03 -13.14,301.22 -6.91,85.64 -12.68,272.48 -15.03,276.56 2.95,28.8 6.87,57.89 11.97,87.34 0.01,0 1.87,0 1.87,0 0,0 10.39,63.38 114.89,533.17 65.87,296.15 32.46,290.84 129.46,580.3 68.86,205.44 149.98,410.67 174.81,627.46 10.6,92.54 1.45,182.46 -0.44,274.97 -0.48,23.23 -11.45,163.09 30.96,163.09 20.38,0 33.89,-205.01 42.11,-439.97 8.66,-247.74 67.61,-485.95 66.48,-723.79 -1.12,-237.84 -62.32,-475.31 -93.11,-721.6 -38.64,-309.08 -85,-625.88 -15.45,-1228.58 40.05,-347.12 125.03,-700.39 148.29,-1054.16 24.41,-371.42 -66.8,-738.84 -37.96,-1109.34 25.95,-333.54 100.63,-663.26 221.21,-975.36 60.05,-155.4 110.66,-310.2 122.71,-476.3 3.16,-43.6 7.5,-87.3 12.51,-131 h -0.03 c 0.61,-5.4 1.29,-10.7 1.93,-16.1 1.28,-10.8 2.53,-21.6 3.9,-32.4 2.25,-18 4.58,-35.9 7.04,-53.7 11.54,-140.8 6.21,-287.798 -46.37,-422.001 -55.79,-142.398 -98.01,-238.297 -103.05,-305.297 -8.48,-112.8 78.86,-139.3 242.17,-167.601 226.67,-39.301 257.51,254.5 251.07,417.5 -3.27,82.801 30.44,208.499 26.04,291.399 0.3,0.2 0.5,0.4 0.48,0.8 0,0 -2.76,57.5 -6.92,147.5 h 0.02 c -0.03,0.7 -0.06,1.3 -0.09,2 -0.39,12.4 -0.79,24.8 -1.05,37.1 h -0.67 c -13.43,292.3 -17.64,841.5 -17.64,1003.45 0,283.15 56.68,424.12 19.24,847.82 -17.71,200.47 -91,412.48 -62.42,612.57 15.46,108.18 92.73,370.9 123.63,811.33 9.85,140.29 20.27,536.01 30.93,726.75 10.65,-190.74 21.07,-586.46 30.92,-726.75 30.9,-440.43 108.17,-703.15 123.63,-811.33 28.58,-200.09 -38.04,-412.81 -62.42,-612.57 -46.83,-383.7 19.24,-564.67 19.24,-847.82 0,-161.95 -4.21,-711.15 -17.64,-1003.45 h -0.67 c -0.26,-12.3 -0.66,-24.7 -1.05,-37.1 -0.03,-0.7 -0.06,-1.3 -0.09,-2 h 0.9 c -4.16,-90 -6.92,-147.5 -6.92,-147.5 -0.02,-0.4 0.18,-0.6 0.48,-0.8 -4.41,-82.9 29.31,-208.598 26.04,-291.399 -6.44,-163 24.4,-456.801 251.07,-417.5 163.31,28.301 250.65,54.801 242.17,167.601 -5.05,67 -47.26,162.899 -103.05,305.297 -52.58,134.203 -57.92,281.201 -46.37,422.001 4.67,33.9 8.96,68 12.87,102.2 h -0.91 c 5.01,43.7 9.35,87.4 12.51,131 12.05,166.1 62.66,320.9 122.71,476.3 120.58,312.1 195.26,641.82 221.21,975.36 28.84,370.5 -62.37,737.92 -37.95,1109.34 23.25,353.77 108.23,707.04 148.28,1054.16 69.55,602.7 23.19,919.5 -15.45,1228.58 -30.79,246.29 -91.99,483.76 -93.12,721.6 -1.12,237.84 57.83,476.05 66.49,723.79 8.22,234.96 21.73,439.97 42.11,439.97 42.41,0 31.44,-139.86 30.96,-163.09 -1.89,-92.51 -11.04,-182.43 -0.44,-274.97 24.83,-216.79 105.95,-422.02 174.81,-627.46 97.01,-289.46 63.59,-284.15 129.47,-580.3 104.49,-469.79 114.87,-533.17 114.87,-533.17 0,0 1.86,0 1.88,0 5.1,-29.45 9.02,-58.54 11.97,-87.34 -2.35,-4.08 -8.12,-190.92 -15.03,-276.56 -7.91,-98.19 -27.34,-202.98 -13.14,-301.22 5.23,-36.11 34.79,-161.73 89.26,-98.1 14.63,17.09 16.36,41.38 17.39,63.85 2.5,54.27 -15.6,178.92 18.61,222.48 32.99,42.01 77.03,12.12 89.76,-32.58 18.52,-65.03 -1.34,-149.07 -23.58,-210.54 -27.29,-75.43 -48.31,-121.36 -96.15,-185.74 -3.23,-4.36 -5.43,-9.27 -6.79,-14.53 -12.13,-46.84 45.34,-80.9 83.06,-50.59 154.52,124.17 241.4,377.38 245.89,556.87 3.92,156.78 -28,306.92 -63.37,457.66"
                        id="path165"
                        style="fill: rgb(154, 253, 230); fill-opacity: 1; fill-rule: nonzero; stroke: none;"></path>
                    </g>
                  </g>
                </svg>
                <div class="age-figure chronological">
                  <i class="bi bi-calendar3"></i>
                </div>
                <span class="age-label">Chronological Age</span>
                <span class="age-value">{{ bioResults.decimalAge }} yrs</span>
              </div>
              <div class="age-item highlight">
                <svg data-v-59c7acfa="" class="bio-age-human" id="bio-age-male-human" height="165"
                  viewBox="250 50 600 1500" style="display:flex">
                  <g data-v-59c7acfa="" id="biog158" transform="matrix(1.3333333,0,0,-1.3333333,0,1600)">
                    <g data-v-59c7acfa="" id="biog160" transform="scale(0.1)">
                      <path data-v-59c7acfa=""
                        d="m 5892.55,6114.67 c 13.77,456.2 3.09,912.57 -27.09,1367.98 -12.68,191.43 -12.21,335.47 -54.9,499.54 -33.52,128.85 -97.3,268.31 -89.09,404 15.19,250.76 -11.93,535.86 -99.01,778.58 -66.27,184.72 -224.21,335.97 -394.53,424.86 -119.24,62.23 -246.92,107.6 -364.23,173.39 -67.6,33.8 -157.04,89.55 -228.36,155.16 -80.83,74.36 -141.23,151.42 -172.13,233.52 -3.16,8.4 -4.82,16.5 -5.32,24.3 0.9,28.1 16.3,52.9 32.4,77.5 20.01,30.5 42.23,65.7 57.29,101 13.48,31.7 21.67,73.3 47.7,99.4 9.21,9.3 18.13,-11.2 41.44,8.9 18.38,15.8 29.04,43.2 34.92,64.9 5.8,21.4 7.1,43.8 12.92,66 9.9,37.9 23.97,76.1 30.87,113 4.53,24.3 8.95,61.2 -11.61,80.8 -9.01,8.5 -37.51,24.5 -43.91,3.3 43.75,145.1 65.75,320.4 48.23,471.8 -6.36,54.9 -22.95,120.3 -57.03,165 -23.25,30.5 -103.76,46.3 -48.53,72.4 -35.37,14.8 -64.47,1.4 -98.34,6.8 -38.75,6.2 -17.57,-3.6 -45.67,35.1 -18.77,25.8 -34.37,26.6 -60.74,39.2 -26.19,12.6 -22.66,11.8 -43.68,33.6 -23.57,-24.6 -27.45,-11.8 -50.23,-9.3 -30.22,3.2 -62.61,-2.3 -92.32,1.9 -33.85,4.9 -60.8,24.7 -96.41,24.9 -35.63,0.2 -61.37,-12 -91.51,-28.8 -44.15,-24.5 -93.22,-21.8 -135.04,-50.6 -46.37,-32 -97.37,-55 -134.6,-100.2 -34.68,-42.1 -70.09,-78.9 -84.14,-132.1 -10.45,-39.5 -20.17,-79.9 -28.25,-120 -25.79,-128.1 -13.33,-284.1 28.78,-407.6 -99.58,17.9 -30.62,-167.7 -24.21,-207.1 10.42,-64 35.97,-133 75.9,-131.9 11.08,0.4 15.68,9.8 20.58,7.8 21.73,-8.9 29.11,-48.4 36.32,-67.7 19.97,-53.4 24.37,-66.4 52.3,-116.1 18.47,-32.8 45.81,-63.5 47.46,-103.2 -4.29,-101.9 -83.76,-179.98 -155.71,-244.46 -76.1,-68.19 -162.01,-117.58 -250.52,-167.22 -117.31,-65.79 -244.99,-111.16 -364.23,-173.39 -170.32,-88.89 -328.26,-240.14 -394.53,-424.86 -87.08,-242.72 -114.2,-527.82 -99.01,-778.58 8.21,-135.69 -55.57,-275.15 -89.09,-404 -42.69,-164.07 -42.22,-308.11 -54.9,-499.54 -30.18,-455.41 -40.86,-911.78 -27.09,-1367.98 -35.37,-150.74 -67.29,-300.88 -63.37,-457.66 4.48,-179.49 91.37,-432.7 245.89,-556.87 37.72,-30.31 95.19,3.75 83.06,50.59 -1.36,5.26 -3.56,10.17 -6.79,14.53 -47.84,64.38 -68.86,110.31 -96.15,185.74 -22.24,61.47 -42.1,145.51 -23.58,210.54 12.73,44.7 56.77,74.59 89.76,32.58 34.21,-43.56 16.11,-168.21 18.61,-222.48 1.03,-22.47 2.76,-46.76 17.39,-63.85 54.47,-63.63 84.03,61.99 89.26,98.1 14.2,98.24 -5.23,203.03 -13.14,301.22 -6.91,85.64 -12.68,272.48 -15.03,276.56 2.95,28.8 6.87,57.89 11.97,87.34 0.01,0 1.87,0 1.87,0 0,0 10.39,63.38 114.89,533.17 65.87,296.15 32.46,290.84 129.46,580.3 68.86,205.44 149.98,410.67 174.81,627.46 10.6,92.54 1.45,182.46 -0.44,274.97 -0.48,23.23 -11.45,163.09 30.96,163.09 20.38,0 33.89,-205.01 42.11,-439.97 8.66,-247.74 67.61,-485.95 66.48,-723.79 -1.12,-237.84 -62.32,-475.31 -93.11,-721.6 -38.64,-309.08 -85,-625.88 -15.45,-1228.58 40.05,-347.12 125.03,-700.39 148.29,-1054.16 24.41,-371.42 -66.8,-738.84 -37.96,-1109.34 25.95,-333.54 100.63,-663.26 221.21,-975.36 60.05,-155.4 110.66,-310.2 122.71,-476.3 3.16,-43.6 7.5,-87.3 12.51,-131 h -0.03 c 0.61,-5.4 1.29,-10.7 1.93,-16.1 1.28,-10.8 2.53,-21.6 3.9,-32.4 2.25,-18 4.58,-35.9 7.04,-53.7 11.54,-140.8 6.21,-287.798 -46.37,-422.001 -55.79,-142.398 -98.01,-238.297 -103.05,-305.297 -8.48,-112.8 78.86,-139.3 242.17,-167.601 226.67,-39.301 257.51,254.5 251.07,417.5 -3.27,82.801 30.44,208.499 26.04,291.399 0.3,0.2 0.5,0.4 0.48,0.8 0,0 -2.76,57.5 -6.92,147.5 h 0.02 c -0.03,0.7 -0.06,1.3 -0.09,2 -0.39,12.4 -0.79,24.8 -1.05,37.1 h -0.67 c -13.43,292.3 -17.64,841.5 -17.64,1003.45 0,283.15 56.68,424.12 19.24,847.82 -17.71,200.47 -91,412.48 -62.42,612.57 15.46,108.18 92.73,370.9 123.63,811.33 9.85,140.29 20.27,536.01 30.93,726.75 10.65,-190.74 21.07,-586.46 30.92,-726.75 30.9,-440.43 108.17,-703.15 123.63,-811.33 28.58,-200.09 -38.04,-412.81 -62.42,-612.57 -46.83,-383.7 19.24,-564.67 19.24,-847.82 0,-161.95 -4.21,-711.15 -17.64,-1003.45 h -0.67 c -0.26,-12.3 -0.66,-24.7 -1.05,-37.1 -0.03,-0.7 -0.06,-1.3 -0.09,-2 h 0.9 c -4.16,-90 -6.92,-147.5 -6.92,-147.5 -0.02,-0.4 0.18,-0.6 0.48,-0.8 -4.41,-82.9 29.31,-208.598 26.04,-291.399 -6.44,-163 24.4,-456.801 251.07,-417.5 163.31,28.301 250.65,54.801 242.17,167.601 -5.05,67 -47.26,162.899 -103.05,305.297 -52.58,134.203 -57.92,281.201 -46.37,422.001 4.67,33.9 8.96,68 12.87,102.2 h -0.91 c 5.01,43.7 9.35,87.4 12.51,131 12.05,166.1 62.66,320.9 122.71,476.3 120.58,312.1 195.26,641.82 221.21,975.36 28.84,370.5 -62.37,737.92 -37.95,1109.34 23.25,353.77 108.23,707.04 148.28,1054.16 69.55,602.7 23.19,919.5 -15.45,1228.58 -30.79,246.29 -91.99,483.76 -93.12,721.6 -1.12,237.84 57.83,476.05 66.49,723.79 8.22,234.96 21.73,439.97 42.11,439.97 42.41,0 31.44,-139.86 30.96,-163.09 -1.89,-92.51 -11.04,-182.43 -0.44,-274.97 24.83,-216.79 105.95,-422.02 174.81,-627.46 97.01,-289.46 63.59,-284.15 129.47,-580.3 104.49,-469.79 114.87,-533.17 114.87,-533.17 0,0 1.86,0 1.88,0 5.1,-29.45 9.02,-58.54 11.97,-87.34 -2.35,-4.08 -8.12,-190.92 -15.03,-276.56 -7.91,-98.19 -27.34,-202.98 -13.14,-301.22 5.23,-36.11 34.79,-161.73 89.26,-98.1 14.63,17.09 16.36,41.38 17.39,63.85 2.5,54.27 -15.6,178.92 18.61,222.48 32.99,42.01 77.03,12.12 89.76,-32.58 18.52,-65.03 -1.34,-149.07 -23.58,-210.54 -27.29,-75.43 -48.31,-121.36 -96.15,-185.74 -3.23,-4.36 -5.43,-9.27 -6.79,-14.53 -12.13,-46.84 45.34,-80.9 83.06,-50.59 154.52,124.17 241.4,377.38 245.89,556.87 3.92,156.78 -28,306.92 -63.37,457.66"
                        id="biopath164"
                        style="fill: rgb(137, 227, 240); fill-opacity: 1; fill-rule: nonzero; stroke: none;"></path>
                    </g>
                  </g>
                </svg>
                <div class="age-figure biological">
                  <i class="bi bi-activity"></i>
                </div>
                <span class="age-label">Biological Age</span>
                <span class="age-value">{{ bioResults.bioAge }} yrs</span>
              </div>
              <div class="age-difference" :class="bioResults.bioAgeDifference > 0 ? 'positive' : 'negative'">
                <span class="diff-label">Difference</span>
                <span class="diff-value">{{ bioResults.bioAgeDifference > 0 ? '+' : '' }}{{ bioResults.bioAgeDifference
                }} yrs</span>
              </div>
            </div>
          </div>

          <!-- Summary Card -->
          <div class="summary-card">
            <div class="summary-item">
              <i class="bi bi-rulers"></i>
              <div class="summary-content">
                <span class="summary-label">Estimated Adult Height</span>
                <span class="summary-value">{{ bioResults.childEstimatedHeight }} cm</span>
              </div>
            </div>
            <div class="summary-item">
              <i class="bi bi-percent"></i>
              <div class="summary-content">
                <span class="summary-label">Height Achieved</span>
                <span class="summary-value">{{ bioResults.childEstimatedHeightAchieved }}%</span>
              </div>
            </div>
            <div class="summary-item">
              <i class="bi bi-clock-history"></i>
              <div class="summary-content">
                <span class="summary-label">Bio Age Difference</span>
                <span class="summary-value" :class="bioResults.bioAgeDifference > 0 ? 'text-success' : 'text-danger'">
                  {{ bioResults.bioAgeDifference > 0 ? '+' : '' }}{{ bioResults.bioAgeDifference }} yrs
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- End Bio-Banding Calculator Tab -->

      <!-- Growth Tracking Tab -->
      <div v-show="activeTab === 'growth'" class="growth-tracking-tab">
        <div v-if="playerId" class="growth-chart-wrapper">
          <GrowthVelocityChart :userId="playerId" :showHistory="true" />
        </div>
        <div v-else class="no-player-message">
          <i class="bi bi-exclamation-circle"></i>
          <p>Please select a player to view growth tracking data.</p>
        </div>
      </div>
      <!-- End Growth Tracking Tab -->
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer" v-if="activeTab === 'calculator' && showResults && bioResults">
      <button class="btn btn-secondary" @click="resetCalculator">
        <i class="bi bi-arrow-counterclockwise"></i> New Calculation
      </button>
      <button class="btn btn-primary" @click="saveResults" :disabled="saving">
        <span v-if="saving" class="spinner-border spinner-border-sm"></span>
        <i v-else class="bi bi-save"></i>
        {{ saving ? 'Saving...' : 'Save Results' }}
      </button>
    </div>
  </div>
</template>

<script>
import { Users } from "@/services/user-service.js";
import { PlayerPhysicalPerformance } from "@/services/player-physical-performance-service.js";
import GrowthVelocityChart from "@/components/GrowthVelocityChart.vue";

export default {
  name: "BioBandingCalculator",
  components: {
    GrowthVelocityChart
  },
  props: {
    playerName: {
      type: String,
      default: ""
    },
    playerId: {
      type: [String, Number],
      default: ""
    },
    playerDob: {
      type: String,
      default: ""
    },
    playerGender: {
      type: String,
      default: ""
    },
    existingResults: {
      type: Object,
      default: null
    },
    currentUserId: {
      type: [String, Number],
      default: null
    }
  },
  emits: ['close', 'results-saved'],
  data() {
    return {
      activeTab: 'calculator',
      isMetricMode: false,
      loading: false,
      saving: false,
      errorMessage: "",
      showResults: false,
      bioInputs: {},
      bioResults: null,
      imperialInputs: {
        fathersHeightFt: null,
        fathersHeightIn: null,
        mothersHeightFt: null,
        mothersHeightIn: null,
        childsDob: "",
        childsHeightFt: null,
        childsHeightIn: null,
        sittingHeightFt: null,
        sittingHeightIn: null,
        childsWeightSt: null,
        childsWeightLb: null,
        gender: ""
      },
      metricInputs: {
        fathersHeight: null,
        mothersHeight: null,
        childsDob: "",
        childsHeight: null,
        sittingHeight: null,
        childsWeight: null,
        gender: ""
      },
      // Khamis-Roche reference data
      khamisRoche: [
        { roundedDecimalAge: 4, gender: "M", bo: -26.0521, stature: 1.23812, weight: -0.48849, midParentStature: 0.50286 },
        { roundedDecimalAge: 5, gender: "M", bo: -27.9942, stature: 1.10674, weight: -0.36274, midParentStature: 0.53919 },
        { roundedDecimalAge: 5.5, gender: "M", bo: -28.3354, stature: 1.0748, weight: -0.32344, midParentStature: 0.53691 },
        { roundedDecimalAge: 6, gender: "M", bo: -28.2291, stature: 1.05923, weight: -0.29649, midParentStature: 0.52513 },
        { roundedDecimalAge: 6.5, gender: "M", bo: -27.9963, stature: 1.05542, weight: -0.27938, midParentStature: 0.50692 },
        { roundedDecimalAge: 7, gender: "M", bo: -27.9361, stature: 1.05877, weight: -0.26959, midParentStature: 0.48538 },
        { roundedDecimalAge: 7.5, gender: "M", bo: -27.9943, stature: 1.06467, weight: -0.26462, midParentStature: 0.46361 },
        { roundedDecimalAge: 8, gender: "M", bo: -28.1169, stature: 1.06853, weight: -0.26194, midParentStature: 0.44469 },
        { roundedDecimalAge: 8.5, gender: "M", bo: -28.2499, stature: 1.06572, weight: -0.25905, midParentStature: 0.43171 },
        { roundedDecimalAge: 9, gender: "M", bo: -28.3392, stature: 1.05161, weight: -0.25343, midParentStature: 0.42777 },
        { roundedDecimalAge: 9.5, gender: "M", bo: -28.3305, stature: 1.02156, weight: -0.2425, midParentStature: 0.43596 },
        { roundedDecimalAge: 10, gender: "M", bo: -28.1694, stature: 0.97091, weight: -0.22374, midParentStature: 0.45936 },
        { roundedDecimalAge: 10.5, gender: "M", bo: -27.8018, stature: 0.89501, weight: -0.19459, midParentStature: 0.50106 },
        { roundedDecimalAge: 11, gender: "M", bo: -27.1735, stature: 0.78918, weight: -0.15248, midParentStature: 0.56416 },
        { roundedDecimalAge: 11.5, gender: "M", bo: -26.231, stature: 0.6497, weight: -0.09485, midParentStature: 0.65176 },
        { roundedDecimalAge: 12, gender: "M", bo: -24.9196, stature: 0.47285, weight: -0.01911, midParentStature: 0.76697 },
        { roundedDecimalAge: 12.5, gender: "M", bo: -23.1848, stature: 0.25492, weight: 0.07734, midParentStature: 0.91289 },
        { roundedDecimalAge: 13, gender: "M", bo: -20.972, stature: -0.00781, weight: 0.19711, midParentStature: 1.09264 },
        { roundedDecimalAge: 13.5, gender: "M", bo: -18.2267, stature: -0.31905, weight: 0.34283, midParentStature: 1.30932 },
        { roundedDecimalAge: 14, gender: "M", bo: -14.8943, stature: -0.68251, weight: 0.51713, midParentStature: 1.56605 },
        { roundedDecimalAge: 14.5, gender: "M", bo: -10.9205, stature: -1.10191, weight: 0.72264, midParentStature: 1.86595 },
        { roundedDecimalAge: 15, gender: "M", bo: -6.2507, stature: -1.58095, weight: 0.96197, midParentStature: 2.21214 },
        { roundedDecimalAge: 15.5, gender: "M", bo: -0.83, stature: -2.12335, weight: 1.23776, midParentStature: 2.60773 },
        { roundedDecimalAge: 16, gender: "M", bo: 5.3952, stature: -2.73281, weight: 1.55262, midParentStature: 3.05585 },
        { roundedDecimalAge: 16.5, gender: "M", bo: 12.4782, stature: -3.41305, weight: 1.90917, midParentStature: 3.55961 },
        { roundedDecimalAge: 17, gender: "M", bo: 20.4726, stature: -4.1678, weight: 2.31001, midParentStature: 4.12213 },
        { roundedDecimalAge: 17.5, gender: "M", bo: 29.4318, stature: -5.00077, weight: 2.75779, midParentStature: 4.74651 },
        { roundedDecimalAge: 4, gender: "F", bo: 30.106, stature: 0.55121, weight: -0.27471, midParentStature: 0.28942 },
        { roundedDecimalAge: 5, gender: "F", bo: 16.4488, stature: 0.6416, weight: -0.20823, midParentStature: 0.34688 },
        { roundedDecimalAge: 5.5, gender: "F", bo: 11.3635, stature: 0.66628, weight: -0.18067, midParentStature: 0.36774 },
        { roundedDecimalAge: 6, gender: "F", bo: 6.7917, stature: 0.68401, weight: -0.15576, midParentStature: 0.38662 },
        { roundedDecimalAge: 6.5, gender: "F", bo: 2.6067, stature: 0.69537, weight: -0.13302, midParentStature: 0.40386 },
        { roundedDecimalAge: 7, gender: "F", bo: -1.2181, stature: 0.70095, weight: -0.11202, midParentStature: 0.41978 },
        { roundedDecimalAge: 7.5, gender: "F", bo: -4.7054, stature: 0.70133, weight: -0.09232, midParentStature: 0.43472 },
        { roundedDecimalAge: 8, gender: "F", bo: -7.878, stature: 0.6971, weight: -0.0735, midParentStature: 0.44901 },
        { roundedDecimalAge: 8.5, gender: "F", bo: -10.7588, stature: 0.68886, weight: -0.05513, midParentStature: 0.46298 },
        { roundedDecimalAge: 9, gender: "F", bo: -13.3706, stature: 0.6772, weight: -0.0368, midParentStature: 0.47697 },
        { roundedDecimalAge: 9.5, gender: "F", bo: -15.7361, stature: 0.66271, weight: -0.01808, midParentStature: 0.49132 },
        { roundedDecimalAge: 10, gender: "F", bo: -17.8783, stature: 0.64601, weight: 0.00144, midParentStature: 0.50635 },
        { roundedDecimalAge: 10.5, gender: "F", bo: -19.8198, stature: 0.6277, weight: 0.02218, midParentStature: 0.5224 },
        { roundedDecimalAge: 11, gender: "F", bo: -21.5834, stature: 0.60838, weight: 0.04457, midParentStature: 0.53981 },
        { roundedDecimalAge: 11.5, gender: "F", bo: -23.1917, stature: 0.58868, weight: 0.06901, midParentStature: 0.55889 },
        { roundedDecimalAge: 12, gender: "F", bo: -24.6675, stature: 0.56918, weight: 0.09592, midParentStature: 0.57999 },
        { roundedDecimalAge: 12.5, gender: "F", bo: -26.0333, stature: 0.55049, weight: 0.1257, midParentStature: 0.60344 },
        { roundedDecimalAge: 13, gender: "F", bo: -27.3118, stature: 0.5332, weight: 0.15876, midParentStature: 0.62958 },
        { roundedDecimalAge: 13.5, gender: "F", bo: -28.5256, stature: 0.51793, weight: 0.19552, midParentStature: 0.65874 },
        { roundedDecimalAge: 14, gender: "F", bo: -29.6973, stature: 0.50527, weight: 0.23639, midParentStature: 0.69127 },
        { roundedDecimalAge: 14.5, gender: "F", bo: -30.8496, stature: 0.49582, weight: 0.28176, midParentStature: 0.72749 },
        { roundedDecimalAge: 15, gender: "F", bo: -32.005, stature: 0.49019, weight: 0.33206, midParentStature: 0.76776 },
        { roundedDecimalAge: 15.5, gender: "F", bo: -33.186, stature: 0.489, weight: 0.38768, midParentStature: 0.81241 },
        { roundedDecimalAge: 16, gender: "F", bo: -34.4153, stature: 0.49285, weight: 0.44902, midParentStature: 0.8618 },
        { roundedDecimalAge: 16.5, gender: "F", bo: -35.7154, stature: 0.50238, weight: 0.5165, midParentStature: 0.91626 },
        { roundedDecimalAge: 17, gender: "F", bo: -37.1089, stature: 0.51819, weight: 0.59052, midParentStature: 0.97613 },
        { roundedDecimalAge: 17.5, gender: "F", bo: -38.6184, stature: 0.5409, weight: 0.67148, midParentStature: 1.04175 }
      ],
      // Predicted Adult Height reference data
      pah: [
        { gender: "M", pah: 28.75, age: 4 }, { gender: "M", pah: 30.81, age: 4.5 }, { gender: "M", pah: 32.72, age: 5 },
        { gender: "M", pah: 34.4, age: 5.5 }, { gender: "M", pah: 35.82, age: 6 }, { gender: "M", pah: 37.01, age: 6.5 },
        { gender: "M", pah: 38.01, age: 7 }, { gender: "M", pah: 38.88, age: 7.5 }, { gender: "M", pah: 39.68, age: 8 },
        { gender: "M", pah: 40.43, age: 8.5 }, { gender: "M", pah: 41.15, age: 9 }, { gender: "M", pah: 41.86, age: 9.5 },
        { gender: "M", pah: 42.53, age: 10 }, { gender: "M", pah: 43.17, age: 10.5 }, { gender: "M", pah: 43.78, age: 11 },
        { gender: "M", pah: 44.38, age: 11.5 }, { gender: "M", pah: 44.95, age: 12 }, { gender: "M", pah: 45.5, age: 12.5 },
        { gender: "M", pah: 46.04, age: 13 }, { gender: "M", pah: 46.56, age: 13.5 }, { gender: "M", pah: 47.05, age: 14 },
        { gender: "M", pah: 47.54, age: 14.5 }, { gender: "M", pah: 48, age: 15 }, { gender: "M", pah: 48.45, age: 15.5 },
        { gender: "M", pah: 48.89, age: 16 }, { gender: "M", pah: 49.32, age: 16.5 }, { gender: "M", pah: 49.74, age: 17 },
        { gender: "M", pah: 50.15, age: 17.5 }, { gender: "M", pah: 50.55, age: 18 },
        { gender: "F", pah: 34.06, age: 4 }, { gender: "F", pah: 36.16, age: 4.5 }, { gender: "F", pah: 38.17, age: 5 },
        { gender: "F", pah: 39.97, age: 5.5 }, { gender: "F", pah: 41.58, age: 6 }, { gender: "F", pah: 43.0, age: 6.5 },
        { gender: "F", pah: 44.28, age: 7 }, { gender: "F", pah: 45.44, age: 7.5 }, { gender: "F", pah: 46.52, age: 8 },
        { gender: "F", pah: 47.53, age: 8.5 }, { gender: "F", pah: 48.5, age: 9 }, { gender: "F", pah: 49.44, age: 9.5 },
        { gender: "F", pah: 50.36, age: 10 }, { gender: "F", pah: 51.27, age: 10.5 }, { gender: "F", pah: 52.18, age: 11 },
        { gender: "F", pah: 53.11, age: 11.5 }, { gender: "F", pah: 54.06, age: 12 }, { gender: "F", pah: 55.05, age: 12.5 },
        { gender: "F", pah: 56.09, age: 13 }, { gender: "F", pah: 57.2, age: 13.5 }, { gender: "F", pah: 58.39, age: 14 },
        { gender: "F", pah: 59.7, age: 14.5 }, { gender: "F", pah: 61.14, age: 15 }, { gender: "F", pah: 62.76, age: 15.5 },
        { gender: "F", pah: 64.6, age: 16 }, { gender: "F", pah: 66.72, age: 16.5 }, { gender: "F", pah: 69.19, age: 17 },
        { gender: "F", pah: 72.12, age: 17.5 }, { gender: "F", pah: 75.64, age: 18 }
      ]
    };
  },
  computed: {
    minDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 18);
      return date.toISOString().split("T")[0];
    },
    maxDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 4);
      return date.toISOString().split("T")[0];
    }
  },
  mounted() {
    // Pre-fill DOB if provided
    if (this.playerDob) {
      this.imperialInputs.childsDob = this.playerDob;
      this.metricInputs.childsDob = this.playerDob;
    }
    // Pre-fill gender if provided
    if (this.playerGender) {
      const gender = this.playerGender.toLowerCase() === 'male' ? 'M' :
        this.playerGender.toLowerCase() === 'female' ? 'F' : '';
      this.imperialInputs.gender = gender;
      this.metricInputs.gender = gender;
    }
    // Load existing results if provided as prop
    if (this.existingResults) {
      this.loadExistingResults();
    } else if (this.playerId) {
      // Fetch existing results from API
      this.fetchExistingResults();
    }
  },
  methods: {
    switchToImperial() {
      this.isMetricMode = false;
      this.showResults = false;
      this.errorMessage = "";
    },
    switchToMetric() {
      this.isMetricMode = true;
      this.showResults = false;
      this.errorMessage = "";
    },
    calculateImperial() {
      this.loading = true;
      this.errorMessage = "";

      try {
        // Convert imperial to metric
        const fathersHeightCm = this.convertFeetInchesToCm(
          this.imperialInputs.fathersHeightFt,
          this.imperialInputs.fathersHeightIn
        );
        const mothersHeightCm = this.convertFeetInchesToCm(
          this.imperialInputs.mothersHeightFt,
          this.imperialInputs.mothersHeightIn
        );
        const childsHeightCm = this.convertFeetInchesToCm(
          this.imperialInputs.childsHeightFt,
          this.imperialInputs.childsHeightIn
        );
        const sittingHeightCm = this.imperialInputs.sittingHeightFt ?
          this.convertFeetInchesToCm(
            this.imperialInputs.sittingHeightFt,
            this.imperialInputs.sittingHeightIn
          ) : null;
        const childsWeightKg = this.convertStonesLbsToKg(
          this.imperialInputs.childsWeightSt,
          this.imperialInputs.childsWeightLb
        );

        this.bioInputs = {
          fathersHeight: fathersHeightCm,
          mothersHeight: mothersHeightCm,
          childsHeight: childsHeightCm,
          sittingHeight: sittingHeightCm,
          childsWeight: childsWeightKg,
          childsDob: this.imperialInputs.childsDob,
          gender: this.imperialInputs.gender
        };

        if (!this.validateInputs()) {
          this.loading = false;
          return;
        }

        this.bioResults = this.calculateBioAge(this.bioInputs);
        this.showResults = true;
      } catch (error) {
        console.error("Error calculating:", error);
        this.errorMessage = "Unable to calculate. Please check your inputs.";
      } finally {
        this.loading = false;
      }
    },
    calculateMetric() {
      this.loading = true;
      this.errorMessage = "";

      try {
        this.bioInputs = {
          fathersHeight: Math.round(this.metricInputs.fathersHeight * 10) / 10,
          mothersHeight: Math.round(this.metricInputs.mothersHeight * 10) / 10,
          childsHeight: Math.round(this.metricInputs.childsHeight * 10) / 10,
          sittingHeight: this.metricInputs.sittingHeight ? Math.round(this.metricInputs.sittingHeight * 10) / 10 : null,
          childsWeight: Math.round(this.metricInputs.childsWeight * 10) / 10,
          childsDob: this.metricInputs.childsDob,
          gender: this.metricInputs.gender
        };

        if (!this.validateInputs()) {
          this.loading = false;
          return;
        }

        this.bioResults = this.calculateBioAge(this.bioInputs);
        this.showResults = true;
      } catch (error) {
        console.error("Error calculating:", error);
        this.errorMessage = "Unable to calculate. Please check your inputs.";
      } finally {
        this.loading = false;
      }
    },
    validateInputs() {
      const inputs = this.bioInputs;

      if (!inputs.fathersHeight || inputs.fathersHeight < 30 || inputs.fathersHeight > 251) {
        this.errorMessage = "Please enter a valid father's height (30-251 cm)";
        return false;
      }
      if (!inputs.mothersHeight || inputs.mothersHeight < 30 || inputs.mothersHeight > 251) {
        this.errorMessage = "Please enter a valid mother's height (30-251 cm)";
        return false;
      }
      if (!inputs.childsHeight || inputs.childsHeight < 30 || inputs.childsHeight > 251) {
        this.errorMessage = "Please enter a valid child's height (30-251 cm)";
        return false;
      }
      if (!inputs.childsWeight || inputs.childsWeight < 1 || inputs.childsWeight > 442) {
        this.errorMessage = "Please enter a valid child's weight (1-442 kg)";
        return false;
      }
      if (!inputs.childsDob) {
        this.errorMessage = "Please enter the child's date of birth";
        return false;
      }
      if (!inputs.gender || (inputs.gender !== 'M' && inputs.gender !== 'F')) {
        this.errorMessage = "Please select a gender";
        return false;
      }

      // Check age range (4-17.5 years)
      const dob = new Date(inputs.childsDob);
      const today = new Date();
      const ageInYears = (today - dob) / (365.25 * 24 * 60 * 60 * 1000);
      if (ageInYears < 4 || ageInYears > 17.5) {
        this.errorMessage = "Child must be between 4 and 17.5 years old";
        return false;
      }

      return true;
    },
    calculateBioAge(inputs) {
      // Calculate decimal age
      const dob = new Date(inputs.childsDob);
      const today = new Date();
      const ms = today.getTime() - dob.getTime();
      const totalDays = Math.ceil(ms / (1000 * 3600 * 24));
      const decimalAge = totalDays / 365.25;
      let decimalAgeRounded = Math.round(decimalAge * 2) / 2;
      const decimalAgeRoundedOne = Math.round(decimalAge * 10) / 10;

      // Handle edge case
      if (decimalAgeRounded === 4.5) {
        decimalAgeRounded = 5;
      }

      // Calculate mid-parent height with adjustment
      const fathersHeightInches = inputs.fathersHeight * 0.3937;
      const mothersHeightInches = inputs.mothersHeight * 0.3937;
      const fathersHeightAdjusted = (fathersHeightInches * 0.955 + 2.316) * 2.54;
      const mothersHeightAdjusted = (mothersHeightInches * 0.953 + 2.803) * 2.54;
      const bioMidParentHeight = (fathersHeightAdjusted + mothersHeightAdjusted) / 2;

      // Get Khamis-Roche coefficients
      const kr = this.khamisRoche.find(
        k => k.roundedDecimalAge === decimalAgeRounded && k.gender === inputs.gender
      );

      if (!kr) {
        throw new Error("No matching Khamis-Roche data found");
      }

      // Calculate estimated height
      const childEstimatedHeight = kr.bo +
        inputs.childsHeight * kr.stature +
        inputs.childsWeight * kr.weight +
        bioMidParentHeight * kr.midParentStature;
      const childEstimatedHeightRounded = Math.round(childEstimatedHeight * 10) / 10;

      // Calculate % height achieved
      const childEstimatedHeightAchieved = (inputs.childsHeight / childEstimatedHeight) * 100;
      const childEstimatedHeightAchievedRounded = Math.round(childEstimatedHeightAchieved * 10) / 10;

      // Calculate bio age from PAH
      const filteredPah = this.pah.filter(p => p.gender === inputs.gender).sort((a, b) => a.pah - b.pah);
      const pahList = filteredPah.map(p => p.pah);
      const pahValue = pahList.reduce((prev, curr) =>
        Math.abs(curr - childEstimatedHeightAchieved) < Math.abs(prev - childEstimatedHeightAchieved) ? curr : prev
      );
      const pahRecord = filteredPah.find(x => x.pah === pahValue);
      const bioAge = pahRecord.age;
      const bioAgeRounded = Math.round(bioAge * 10) / 10;

      // Calculate difference
      const bioAgeDiff = bioAge - decimalAgeRoundedOne;
      const bioAgeDiffRounded = Math.round(bioAgeDiff * 10) / 10;

      return {
        decimalAge: decimalAgeRoundedOne,
        childEstimatedHeight: childEstimatedHeightRounded,
        childEstimatedHeightAchieved: childEstimatedHeightAchievedRounded,
        bioAge: bioAgeRounded,
        bioAgeDifference: bioAgeDiffRounded
      };
    },
    convertFeetInchesToCm(feet, inches) {
      const ft = parseFloat(feet) || 0;
      const inc = parseFloat(inches) || 0;
      return Math.round((ft * 30.48 + inc * 2.54) * 10) / 10;
    },
    convertStonesLbsToKg(stones, lbs) {
      const st = parseFloat(stones) || 0;
      const lb = parseFloat(lbs) || 0;
      return Math.round((st * 6.35029 + lb * 0.453592) * 10) / 10;
    },
    getHeightPercent(height) {
      // Scale height to percentage (assuming max ~220cm)
      const maxHeight = 220;
      return Math.min((height / maxHeight) * 100, 100);
    },
    resetCalculator() {
      this.showResults = false;
      this.bioResults = null;
      this.errorMessage = "";
    },
    async saveResults() {
      if (!this.playerId || !this.bioResults) return;

      this.saving = true;
      try {
        // Save to measurement history first (for tracking over time)
        await PlayerPhysicalPerformance.saveBioAgeMeasurement({
          userId: this.playerId,
          childsHeight: this.bioInputs.childsHeight,
          childsWeight: this.bioInputs.childsWeight,
          sittingHeight: this.bioInputs.sittingHeight,
          fathersHeight: this.bioInputs.fathersHeight,
          mothersHeight: this.bioInputs.mothersHeight,
          gender: this.bioInputs.gender,
          decimalAge: this.bioResults.decimalAge,
          percentPAH: this.bioResults.childEstimatedHeightAchieved,
          bioAge: this.bioResults.bioAge,
          bioAgeDifference: this.bioResults.bioAgeDifference,
          childEstimatedHeight: this.bioResults.childEstimatedHeight,
          childsDob: this.bioInputs.childsDob,
          createdBy: this.currentUserId,
        });

        // Save to current results table
        await PlayerPhysicalPerformance.find({
          action: "saveBioAgeResults",
          playerId: this.playerId,
          bioInputs: JSON.stringify(this.bioInputs),
          bioResults: JSON.stringify(this.bioResults)
        });

        this.$emit('results-saved', {
          inputs: this.bioInputs,
          results: this.bioResults
        });

        // Show success message if toast is available
        if (this.$toast) {
          this.$toast.success("Bio-banding results saved successfully!");
        }
      } catch (error) {
        console.error("Error saving results:", error);
        if (this.$toast) {
          this.$toast.error("Failed to save results");
        }
      } finally {
        this.saving = false;
      }
    },
    loadExistingResults() {
      if (this.existingResults?.inputs) {
        this.bioInputs = { ...this.existingResults.inputs };
        // Populate metric inputs
        this.metricInputs = {
          fathersHeight: this.bioInputs.fathersHeight,
          mothersHeight: this.bioInputs.mothersHeight,
          childsDob: this.bioInputs.childsDob,
          childsHeight: this.bioInputs.childsHeight,
          sittingHeight: this.bioInputs.sittingHeight,
          childsWeight: this.bioInputs.childsWeight,
          gender: this.bioInputs.gender
        };
      }
      if (this.existingResults?.results) {
        this.bioResults = { ...this.existingResults.results };
        this.showResults = true;
      }
    },
    async fetchExistingResults() {
      if (!this.playerId) return;

      try {
        const response = await PlayerPhysicalPerformance.getBioAgeResults(this.playerId);
        const data = response.data;

        // Check if we have valid results from the API
        if (data && data.bioAge) {
          // Populate bioInputs from API response
          this.bioInputs = {
            fathersHeight: parseFloat(data.fathersHeight) || null,
            mothersHeight: parseFloat(data.mothersHeight) || null,
            childsHeight: parseFloat(data.childsHeight) || null,
            sittingHeight: parseFloat(data.sittingHeight) || null,
            childsWeight: parseFloat(data.childsWeight) || null,
            childsDob: data.childsDob || this.playerDob || '',
            gender: data.gender || ''
          };

          // Populate metric inputs for the form
          this.metricInputs = {
            fathersHeight: this.bioInputs.fathersHeight,
            mothersHeight: this.bioInputs.mothersHeight,
            childsDob: this.bioInputs.childsDob,
            childsHeight: this.bioInputs.childsHeight,
            sittingHeight: this.bioInputs.sittingHeight,
            childsWeight: this.bioInputs.childsWeight,
            gender: this.bioInputs.gender
          };

          // Populate bioResults from API response
          this.bioResults = {
            decimalAge: parseFloat(data.decimalAge) || null,
            childEstimatedHeight: parseFloat(data.childEstimatedHeight) || null,
            childEstimatedHeightAchieved: parseFloat(data.childEstimatedHeightAchieved) || null,
            bioAge: parseFloat(data.bioAge) || null,
            bioAgeDifference: parseFloat(data.bioAgeDifference) || null
          };

          // Show results panel
          this.showResults = true;
        }
      } catch (error) {
        console.error("Error fetching existing bio-age results:", error);
        // Silently fail - user can still enter new data
      }
    }
  }
};
</script>

<style scoped lang="scss">
.bioband-calculator-modal {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;

  .modal-title {
    display: flex;
    align-items: center;
    gap: 12px;

    i {
      font-size: 24px;
      color: #007bff;
    }

    h3 {
      margin: 0;
      font-size: 20px;
    }
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: #6c757d;
    line-height: 1;
    padding: 0;

    &:hover {
      color: #000;
    }
  }
}

.modal-tabs {
  display: flex;
  border-bottom: 2px solid #e9ecef;
  padding: 0 24px;
  background: #f8f9fa;

  .tab-btn {
    padding: 12px 20px;
    border: none;
    background: none;
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 3px solid transparent;
    margin-bottom: -2px;
    transition: all 0.2s;

    i {
      font-size: 16px;
    }

    &:hover {
      color: #007bff;
      background: rgba(0, 123, 255, 0.05);
    }

    &.active {
      color: #007bff;
      border-bottom-color: #007bff;
      background: white;
    }
  }
}

.modal-body {
  padding: 24px;
  flex: 1;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  position: sticky;
  bottom: 0;
  background: white;
}

.player-info-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  margin-bottom: 20px;

  i {
    font-size: 18px;
  }
}

.measurement-toggle {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;

  .toggle-option {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    border: 2px solid #dee2e6;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;

    input {
      display: none;
    }

    &:hover {
      border-color: #007bff;
    }

    &.active {
      background: #007bff;
      border-color: #007bff;
      color: white;
    }
  }
}

.bioband-form {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 20px;

  .form-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .form-group {
    flex: 1;
    min-width: 200px;

    label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      font-size: 14px;
    }

    .optional-label {
      color: #6c757d;
      font-weight: 400;
    }

    .form-hint {
      display: block;
      margin-top: 4px;
      font-size: 12px;
      color: #6c757d;
    }

    &.form-group-btn {
      display: flex;
      align-items: flex-end;
    }
  }

  .input-group {
    display: flex;
    gap: 8px;

    .form-control {
      flex: 1;
    }
  }

  .gender-toggle {
    display: flex;
    gap: 12px;

    .gender-option {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      border: 2px solid #dee2e6;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s;

      input {
        display: none;
      }

      &:has(input:checked) {
        background: #e3f2fd;
        border-color: #007bff;
        color: #007bff;
      }
    }
  }
}

.calculate-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  font-weight: 500;
}

.error-pane {
  margin-bottom: 20px;

  .alert {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;

    i {
      font-size: 20px;
    }
  }
}

.bioband-results {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px;

  .results-title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 24px;
    font-size: 18px;

    i {
      color: #28a745;
    }
  }
}

.results-section {
  background: white;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;

  h5 {
    margin-bottom: 16px;
    color: #495057;
    font-size: 16px;
  }
}

.heights-display {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  gap: 20px;
  min-height: 200px;
  padding: 20px 0;
}

.height-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  max-width: 100px;

  &.highlight {

    .height-label,
    .height-value {
      font-weight: 600;
      color: #ffc107;
    }
  }

  .height-figure {
    width: 50px;
    min-height: 40px;
    display: flex;
    align-items: flex-end;
    justify-content: center;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .height-label {
    font-size: 12px;
    color: #6c757d;
  }

  .height-value {
    font-size: 14px;
    font-weight: 500;
  }
}

.progress-section {
  margin-top: 20px;

  label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    color: #495057;
  }

  .progress {
    height: 24px;
    border-radius: 12px;
    background: #e9ecef;

    .progress-bar {
      background: linear-gradient(90deg, #ffc107, #ffeb3b);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      color: #333;
    }
  }
}

.age-comparison {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 40px;
  padding: 20px;
}

.age-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  &.highlight {
    .age-figure {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }
  }

  .age-figure {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e9ecef;

    i {
      font-size: 24px;
      color: white;
    }

    &.chronological {
      background: #17a2b8;
    }

    &.biological {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }
  }

  .age-label {
    font-size: 12px;
    color: #6c757d;
  }

  .age-value {
    font-size: 18px;
    font-weight: 600;
  }
}

.age-difference {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 20px;
  border-radius: 8px;

  &.positive {
    background: #d4edda;
    color: #155724;
  }

  &.negative {
    background: #f8d7da;
    color: #721c24;
  }

  .diff-label {
    font-size: 12px;
    opacity: 0.8;
  }

  .diff-value {
    font-size: 20px;
    font-weight: 700;
  }
}

.summary-card {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;

  .summary-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    i {
      font-size: 24px;
      color: #007bff;
    }

    .summary-content {
      display: flex;
      flex-direction: column;
    }

    .summary-label {
      font-size: 12px;
      color: #6c757d;
    }

    .summary-value {
      font-size: 18px;
      font-weight: 600;
    }
  }
}

.growth-tracking-tab {
  min-height: 400px;

  .growth-chart-wrapper {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 20px;
  }
}

.no-player-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #6c757d;

  i {
    font-size: 48px;
    margin-bottom: 16px;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

@media (max-width: 768px) {
  .bioband-calculator-modal {
    border-radius: 0;
    max-height: 100vh;
  }

  .modal-tabs {
    padding: 0 16px;

    .tab-btn {
      padding: 10px 12px;
      font-size: 12px;
    }
  }

  .modal-body {
    padding: 16px;
  }

  .bioband-form {
    padding: 16px;

    .form-row {
      flex-direction: column;
    }

    .form-group {
      min-width: 100%;
    }
  }

  .heights-display {
    flex-wrap: wrap;
    min-height: auto;
  }

  .height-item {
    max-width: none;
    width: calc(50% - 10px);
  }

  .age-comparison {
    flex-direction: column;
    gap: 20px;
  }
}
</style>
